import React, { useState, useEffect } from "react";
import {
  UserCheck,
  UserX,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  Filter,
  History,
  Award,
  RefreshCw,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import apiService from "../services/api";
import { useAuth } from "../context/AuthContext";
import "../styles/EmployeeApproval.css";

const EmployeeApproval = () => {
  const { user, hasPermission } = useAuth();
  const [pendingEmployees, setPendingEmployees] = useState([]);
  const [approvalLogs, setApprovalLogs] = useState([]);
  const [recruiterStats, setRecruiterStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending");
  const [selectedIds, setSelectedIds] = useState([]);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [expandedStats, setExpandedStats] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [pendingRes, logsRes, statsRes] = await Promise.all([
        apiService.getNewEmployees("pending"),
        apiService.getApprovalLogs(50),
        apiService.getRecruiterStats(),
      ]);

      setPendingEmployees(pendingRes.new_employees || []);
      setApprovalLogs(logsRes.logs || []);
      setRecruiterStats(statsRes.stats || []);
    } catch (error) {
      console.error("Failed to load data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      const response = await apiService.approveNewEmployee(id);
      if (response.success) {
        loadData();
      }
    } catch (error) {
      console.error("Failed to approve:", error);
      alert(error.response?.data?.error || "שגיאה באישור העובד");
    }
  };

  const handleReject = async () => {
    if (!rejectingId) return;
    try {
      const response = await apiService.rejectNewEmployee(
        rejectingId,
        rejectReason
      );
      if (response.success) {
        setShowRejectModal(false);
        setRejectingId(null);
        setRejectReason("");
        loadData();
      }
    } catch (error) {
      console.error("Failed to reject:", error);
      alert(error.response?.data?.error || "שגיאה בדחיית העובד");
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) return;
    try {
      const response = await apiService.bulkApproveNewEmployees(selectedIds);
      if (response.success) {
        setSelectedIds([]);
        loadData();
        alert(`אושרו ${response.approved_count} עובדים בהצלחה`);
      }
    } catch (error) {
      console.error("Failed to bulk approve:", error);
      alert(error.response?.data?.error || "שגיאה באישור קבוצתי");
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === pendingEmployees.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(pendingEmployees.map((e) => e.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("he-IL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className='approval-page'>
      <div className='approval-header'>
        <div className='header-content'>
          <div className='header-icon'>
            <UserCheck size={32} />
          </div>
          <div className='header-text'>
            <h1>אישור עובדים חדשים</h1>
            <p>בדוק ואשר עובדים שהוגשו על ידי מגייסים</p>
          </div>
        </div>
        <button className='btn-refresh' onClick={loadData} disabled={loading}>
          <RefreshCw size={18} className={loading ? "spinning" : ""} />
          רענן
        </button>
      </div>

      {/* Recruiter Stats Summary */}
      <div className='stats-card'>
        <div
          className='stats-header'
          onClick={() => setExpandedStats(!expandedStats)}
        >
          <div className='stats-title'>
            <Award size={20} />
            <span>סטטיסטיקות מגייסים</span>
          </div>
          {expandedStats ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
        {expandedStats && (
          <div className='stats-content'>
            {recruiterStats.length === 0 ? (
              <p className='no-data'>אין נתונים עדיין</p>
            ) : (
              <table className='stats-table'>
                <thead>
                  <tr>
                    <th>מגייס</th>
                    <th>אושרו</th>
                    <th>ממתינים</th>
                  </tr>
                </thead>
                <tbody>
                  {recruiterStats.map((stat, idx) => (
                    <tr key={idx}>
                      <td>{stat.recruiter_username || "לא ידוע"}</td>
                      <td className='approved-count'>{stat.total_approved}</td>
                      <td className='pending-count'>
                        {stat.pending_count || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className='tabs'>
        <button
          className={`tab ${activeTab === "pending" ? "active" : ""}`}
          onClick={() => setActiveTab("pending")}
        >
          <Clock size={18} />
          ממתינים לאישור
          {pendingEmployees.length > 0 && (
            <span className='badge'>{pendingEmployees.length}</span>
          )}
        </button>
        <button
          className={`tab ${activeTab === "logs" ? "active" : ""}`}
          onClick={() => setActiveTab("logs")}
        >
          <History size={18} />
          היסטוריית אישורים
        </button>
      </div>

      {/* Content */}
      <div className='approval-content'>
        {activeTab === "pending" && (
          <>
            {pendingEmployees.length > 0 && (
              <div className='bulk-actions'>
                <label className='select-all'>
                  <input
                    type='checkbox'
                    checked={selectedIds.length === pendingEmployees.length}
                    onChange={toggleSelectAll}
                  />
                  <span>בחר הכל ({pendingEmployees.length})</span>
                </label>
                {selectedIds.length > 0 && (
                  <button
                    className='btn-bulk-approve'
                    onClick={handleBulkApprove}
                  >
                    <CheckCircle size={18} />
                    אשר נבחרים ({selectedIds.length})
                  </button>
                )}
              </div>
            )}

            {loading ? (
              <div className='loading'>טוען...</div>
            ) : pendingEmployees.length === 0 ? (
              <div className='empty-state'>
                <Users size={48} />
                <h3>אין עובדים ממתינים</h3>
                <p>כל העובדים שהוגשו אושרו או נדחו</p>
              </div>
            ) : (
              <div className='employees-list'>
                {pendingEmployees.map((emp) => (
                  <div key={emp.id} className='employee-card'>
                    <div className='card-checkbox'>
                      <input
                        type='checkbox'
                        checked={selectedIds.includes(emp.id)}
                        onChange={() => toggleSelect(emp.id)}
                      />
                    </div>
                    <div className='card-content'>
                      <div className='card-header'>
                        <h3>{emp.employee_name}</h3>
                        <span className='recruiter-tag'>
                          מגייס:{" "}
                          {emp.recruiter || emp.uploader_name || "לא ידוע"}
                        </span>
                      </div>
                      <div className='card-details'>
                        <div className='detail'>
                          <span className='label'>לקוח:</span>
                          <span>{emp.customer_name || "-"}</span>
                        </div>
                        <div className='detail'>
                          <span className='label'>אתר:</span>
                          <span>{emp.site_name || "-"}</span>
                        </div>
                        <div className='detail'>
                          <span className='label'>תעריף:</span>
                          <span>
                            {emp.hour_rate ? `₪${emp.hour_rate}` : "-"}
                          </span>
                        </div>
                        <div className='detail'>
                          <span className='label'>סוג:</span>
                          <span>{emp.type || "-"}</span>
                        </div>
                        <div className='detail'>
                          <span className='label'>תאריך הגשה:</span>
                          <span>{formatDate(emp.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    <div className='card-actions'>
                      <button
                        className='btn-approve'
                        onClick={() => handleApprove(emp.id)}
                      >
                        <CheckCircle size={18} />
                        אשר
                      </button>
                      <button
                        className='btn-reject'
                        onClick={() => {
                          setRejectingId(emp.id);
                          setShowRejectModal(true);
                        }}
                      >
                        <XCircle size={18} />
                        דחה
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === "logs" && (
          <div className='logs-container'>
            {approvalLogs.length === 0 ? (
              <div className='empty-state'>
                <History size={48} />
                <h3>אין היסטוריה</h3>
                <p>טרם בוצעו אישורים או דחיות</p>
              </div>
            ) : (
              <table className='logs-table'>
                <thead>
                  <tr>
                    <th>עובד</th>
                    <th>פעולה</th>
                    <th>מאשר</th>
                    <th>מגייס</th>
                    <th>לקוח</th>
                    <th>תעריף</th>
                    <th>תאריך</th>
                  </tr>
                </thead>
                <tbody>
                  {approvalLogs.map((log) => (
                    <tr key={log.id}>
                      <td className='employee-name'>{log.new_employee_name}</td>
                      <td>
                        <span className={`action-badge ${log.action}`}>
                          {log.action === "approved" ? "אושר" : "נדחה"}
                        </span>
                      </td>
                      <td>{log.approved_by_username || "-"}</td>
                      <td>{log.recruiter_username || "-"}</td>
                      <td>{log.customer_name || "-"}</td>
                      <td>{log.hour_rate ? `₪${log.hour_rate}` : "-"}</td>
                      <td>{formatDate(log.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div
          className='modal-overlay'
          onClick={() => setShowRejectModal(false)}
        >
          <div className='modal-content' onClick={(e) => e.stopPropagation()}>
            <h3>דחיית עובד</h3>
            <p>אנא ציין סיבת הדחייה:</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder='סיבת הדחייה...'
              rows={3}
            />
            <div className='modal-actions'>
              <button
                className='btn-cancel'
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectingId(null);
                  setRejectReason("");
                }}
              >
                ביטול
              </button>
              <button className='btn-confirm-reject' onClick={handleReject}>
                אשר דחייה
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeApproval;
